import { Extensions } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table'
import { Embed } from './EmbedNode'

export type RichTextVariant = 'default' | 'article'

// Links to our own site are stored root-relative (/begravelse/), so they survive a domain move
const OWN_SITE_PREFIX = /^https?:\/\/(www\.)?find-bedemand\.dk(?=[/?#]|$)/i

export function normalizeHref(href: string): string {
  const trimmed = href.trim()
  if (!OWN_SITE_PREFIX.test(trimmed)) return trimmed
  return trimmed.replace(OWN_SITE_PREFIX, '') || '/'
}

// Internal = root-relative path, #anchor or an absolute link to find-bedemand.dk
export function isInternalHref(href: string | null | undefined): boolean {
  const trimmed = (href || '').trim()
  return /^[/#]/.test(trimmed) || OWN_SITE_PREFIX.test(trimmed)
}

/**
 * Link without Tiptap's default target="_blank" rel="noopener noreferrer nofollow".
 * Internal links also drop any target/rel already stored (left there by those old defaults),
 * so re-saving a text removes nofollow from internal links. External links keep what they have.
 */
const PlainLink = Link.extend({
  addAttributes() {
    const parent = (this.parent?.() || {}) as Record<string, any>
    const keepOnExternalLinks = (name: 'target' | 'rel') => ({
      ...parent[name],
      parseHTML: (element: HTMLElement) =>
        isInternalHref(element.getAttribute('href')) ? null : element.getAttribute(name),
    })
    return {
      ...parent,
      target: keepOnExternalLinks('target'),
      rel: keepOnExternalLinks('rel'),
    }
  },
})

const PLAIN_LINK_ATTRIBUTES = { target: null, rel: null, class: null }

// Table output without the colgroup/min-width styles Tiptap adds by default
const CleanTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    return ['table', HTMLAttributes, ['tbody', 0]]
  },
})

// Only write colspan/rowspan when they mean something, never colwidth
function cleanCellAttributes(parentAttributes: unknown) {
  const parent = (parentAttributes || {}) as Record<string, any>
  return {
    ...parent,
    colspan: {
      ...parent.colspan,
      renderHTML: (attrs: Record<string, any>) => (attrs.colspan > 1 ? { colspan: attrs.colspan } : {}),
    },
    rowspan: {
      ...parent.rowspan,
      renderHTML: (attrs: Record<string, any>) => (attrs.rowspan > 1 ? { rowspan: attrs.rowspan } : {}),
    },
    colwidth: {
      ...parent.colwidth,
      renderHTML: () => ({}),
    },
  }
}

const CleanTableHeader = TableHeader.extend({
  addAttributes() {
    return cleanCellAttributes(this.parent?.())
  },
})

const CleanTableCell = TableCell.extend({
  addAttributes() {
    return cleanCellAttributes(this.parent?.())
  },
})

export function getEditorExtensions(variant: RichTextVariant, placeholder?: string): Extensions {
  if (variant === 'article') {
    return [
      StarterKit.configure({
        heading: {
          levels: [2, 3, 4],
        },
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
        // Blockquote is used as "Faktaboks" in articles
        blockquote: {},
        // No automatic empty <p></p> after a final table or embed (Enter or the gap cursor adds one)
        trailingNode: false,
        // StarterKit v3 includes Link; replaced by PlainLink below
        link: false,
      }),
      PlainLink.configure({
        openOnClick: false,
        autolink: false,
        // No target/rel/class by default: internal links must stay plain <a href="/sti/">
        HTMLAttributes: PLAIN_LINK_ATTRIBUTES,
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Skriv her...',
      }),
      CleanTable.configure({ resizable: false }),
      TableRow,
      CleanTableHeader,
      CleanTableCell,
      Embed,
    ]
  }

  return [
    StarterKit.configure({
      // Only allow these block types
      heading: {
        levels: [2, 3, 4],
      },
      // Disable features we don't want
      codeBlock: false,
      code: false,
      blockquote: false,
      horizontalRule: false,
      strike: false,
      hardBreak: {},
      // Keep these enabled
      paragraph: {},
      bold: {},
      italic: {},
      bulletList: {},
      orderedList: {},
      listItem: {},
      // StarterKit v3 includes Underline (no separate extension needed) and Link,
      // which is replaced by PlainLink below. Pasted links are still stripped by stripPastedHTML.
      link: false,
    }),
    PlainLink.configure({
      openOnClick: false,
      HTMLAttributes: PLAIN_LINK_ATTRIBUTES,
    }),
    Placeholder.configure({
      placeholder: placeholder || 'Skriv her...',
    }),
  ]
}

// Default variant: strip links, images and iframes from pasted HTML
export function stripPastedHTML(html: string): string {
  // Remove all <a> tags but keep their text content
  const temp = document.createElement('div')
  temp.innerHTML = html

  // Remove links
  temp.querySelectorAll('a').forEach(link => {
    const text = document.createTextNode(link.textContent || '')
    link.parentNode?.replaceChild(text, link)
  })

  // Remove images
  temp.querySelectorAll('img').forEach(img => img.remove())

  // Remove iframes
  temp.querySelectorAll('iframe').forEach(iframe => iframe.remove())

  return temp.innerHTML
}

// Article variant: keep links (href only), remove images and iframes
export function cleanPastedArticleHTML(html: string): string {
  const temp = document.createElement('div')
  temp.innerHTML = html

  temp.querySelectorAll('a').forEach(link => {
    const href = link.getAttribute('href')
    // Drop target/rel/class/style from Word, Google Docs and other sites
    Array.from(link.attributes).forEach(attr => link.removeAttribute(attr.name))
    if (href) link.setAttribute('href', normalizeHref(href))
  })

  temp.querySelectorAll('img').forEach(img => img.remove())
  temp.querySelectorAll('iframe').forEach(iframe => iframe.remove())

  return temp.innerHTML
}
